import React from 'react';
import { GoogleMap, MarkerF, InfoWindowF } from '@react-google-maps/api';
import { Location } from '../types';
import './DeviceMap.css';

interface DeviceMapProps {
  location: Location | null;
  deviceName: string;
}

const DeviceMap: React.FC<DeviceMapProps> = ({ location, deviceName }) => {
  const [showInfo, setShowInfo] = React.useState(false);
  const [loadError, setLoadError] = React.useState<string | null>(null);

  // Default to Guadalajara, Mexico if no location
  const defaultPosition = { lat: 20.49965797726723, lng: -103.49207326019203 };
  const position = location
    ? { lat: location.latitude, lng: location.longitude }
    : defaultPosition;

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const mapContainerStyle = {
    width: '100%',
    height: '450px',
    borderRadius: '10px'
  };

  const mapOptions = {
    disableDefaultUI: false,
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: true,
    streetViewControl: false,
    rotateControl: false,
    fullscreenControl: true,
  };

  return (
    <div className="device-map-card">
      <div className="map-header">
        <div className="map-title">
          <svg className="map-icon" width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h3>Device Location</h3>
        </div>
        {location && (
          <div className="location-info">
            <span className="location-badge">
              <svg className="badge-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              {formatTimestamp(location.timestamp)}
            </span>
          </div>
        )}
      </div>

      {loadError && (
        <div className="map-error-message">
          <svg className="error-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
            <path d="M12 8V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="12" cy="16" r="1" fill="currentColor"/>
          </svg>
          <div>
            <strong>Google Maps Error</strong>
            <p>{loadError}</p>
            <p className="help-text">
              Check the browser console for details or verify the API key configuration in Google Cloud Console.
            </p>
          </div>
        </div>
      )}

      <div className="map-wrapper">
        <div className="map-container">
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={position}
            zoom={15}
            options={mapOptions}
            onLoad={() => console.log('Map instance loaded')}
          >
            <MarkerF
              position={position}
              onClick={() => setShowInfo(!showInfo)}
            />

            {showInfo && (
              <InfoWindowF
                position={position}
                onCloseClick={() => setShowInfo(false)}
              >
                <div className="map-popup">
                  <h4>{deviceName}</h4>
                  <div className="popup-details">
                    <div className="popup-row">
                      <svg className="popup-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                        <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      <span>{location ? formatTimestamp(location.timestamp) : 'Unknown'}</span>
                    </div>
                    <div className="popup-row">
                      <svg className="popup-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 10C21 17 12 23 12 23 C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2"/>
                        <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <span className="coordinates">
                        {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
                      </span>
                    </div>
                  </div>
                </div>
              </InfoWindowF>
            )}
          </GoogleMap>
        </div>
      </div>
    </div>
  );
};

export default DeviceMap;
